/**
 * Five — mini-hub.
 *
 * Two cards: Daily Word + Practice. The page composition is a quieter
 * version of the in-game page: same cream paper, no tile grid yet, but a
 * teaser composition where five letterpress tiles spell the word "FIVE-"
 * with the trailing dash empty (a small tease — there's always one more
 * tile to fill).
 *
 * Cards link to `/play/five?mode=daily` and `/play/five?mode=practice`.
 */
import { motion } from 'motion/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { usePreferences } from '@/preferences';

import { cardSpring, HUB_STAGGER_S, hoverSpring, pageSpring } from './motion';
import { sound } from './sound';

import './FiveHub.css';

interface ModeCard {
  slug: 'daily' | 'practice';
  title: string;
  eyebrow: string;
  blurb: string;
  letters: string;
  /** which tile indices are "revealed" — used to tease the look. */
  revealed: ReadonlyArray<'placed' | 'present' | 'absent'>;
}

const MODES: ReadonlyArray<ModeCard> = [
  {
    slug: 'daily',
    title: 'Daily Word',
    eyebrow: 'Today, together',
    blurb: 'A new word every day. Everyone in your community plays the same one.',
    letters: 'TODAY',
    revealed: ['placed', 'present', 'absent', 'placed', 'placed'],
  },
  {
    slug: 'practice',
    title: 'Practice',
    eyebrow: 'Any time',
    blurb: 'A fresh random word, as many as you like. No streak, no clock.',
    letters: 'AGAIN',
    revealed: ['absent', 'placed', 'present', 'absent', 'placed'],
  },
];

export function FiveHub() {
  const navigate = useNavigate();
  const { theme } = usePreferences();

  // Soft whoosh on entry — only once per mount.
  useEffect(() => {
    const t = window.setTimeout(() => {
      sound.unlock();
      sound.play('whoosh');
    }, 80);
    return () => window.clearTimeout(t);
  }, []);

  const pick = (slug: 'daily' | 'practice'): void => {
    sound.unlock();
    sound.play('whoosh');
    navigate(`/play/five?mode=${slug}`);
  };

  return (
    <div className="five-hub" data-theme={theme}>
      <div className="five-hub__desk" aria-hidden="true" />
      <div className="five-hub__page">
        <PaperBackdrop />

        <motion.header
          className="five-hub__head"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...pageSpring, delay: 0.05 }}
        >
          <p className="five-hub__eyebrow">A five-letter game</p>
          <h1 className="five-hub__title">
            <span>Five.</span>
          </h1>
          <p className="five-hub__lede">
            Find the hidden word in six guesses. Each letter you place tells
            you something true about the answer. No clock, no streak — just
            a quiet thinker&rsquo;s puzzle.
          </p>
        </motion.header>

        <section className="five-hub__choices" aria-label="Choose how to play">
          <ul className="five-hub__cards" role="list">
            {MODES.map((m, i) => (
              <motion.li
                key={m.slug}
                className="five-hub__card-item"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...cardSpring, delay: 0.15 + i * HUB_STAGGER_S }}
                whileHover={{ y: -4, transition: hoverSpring }}
              >
                <button
                  type="button"
                  className="five-hub__card"
                  data-slug={m.slug}
                  onClick={() => pick(m.slug)}
                  onPointerEnter={() => sound.play('keyTap')}
                >
                  <span className="five-hub__card-eyebrow">{m.eyebrow}</span>
                  <span className="five-hub__card-title">{m.title}</span>

                  <span className="five-hub__teaser" aria-hidden="true">
                    {m.letters.split('').map((ch, idx) => (
                      <span
                        key={idx}
                        className="five-hub__teaser-tile"
                        data-state={m.revealed[idx]}
                      >
                        <span>{ch}</span>
                      </span>
                    ))}
                  </span>

                  <span className="five-hub__card-blurb">{m.blurb}</span>

                  <span className="five-hub__card-cta" aria-hidden="true">
                    Play →
                  </span>
                </button>
              </motion.li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paper backdrop — shared visual register
// ---------------------------------------------------------------------------

function PaperBackdrop() {
  return (
    <>
      <div className="five-hub__paper" aria-hidden="true">
        <svg width="100%" height="100%" aria-hidden="true">
          <defs>
            <filter id="five-hub-grain" x="0" y="0" width="100%" height="100%">
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.82"
                numOctaves="2"
                seed="9"
              />
              <feColorMatrix values="0 0 0 0 0.14  0 0 0 0 0.11  0 0 0 0 0.07  0 0 0 0.07 0" />
            </filter>
          </defs>
          <rect width="100%" height="100%" filter="url(#five-hub-grain)" />
        </svg>
      </div>
      <div className="five-hub__paper-edge" aria-hidden="true" />
    </>
  );
}
